import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  from,
  mergeMap,
  Observable,
} from 'rxjs';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { environment } from 'src/environments/environment';
import { EpisodeOfCareService } from './episodeOfCare.service';
import { PatientService } from './patient.service';
import { IAddVelesMocaAssessment } from '../interfaces/Veles/IAddVelesMocaAssessment';
import { IVelesMocaAssessment } from '../interfaces/Veles/IVelesMocaAssessment';
import { saveAs } from 'file-saver';
import { SwalToastService } from './swal.service';
import { ALLOWED_MIME, validateUpload } from '../guards/file.guard';

@Injectable({ providedIn: 'root' })
export class VelesMocaAssessmentService {
  public mocaModalSub = new BehaviorSubject<boolean>(false);

  public mocaDataSub = new BehaviorSubject<IVelesMocaAssessment[]>([]);

  public mocaLoadingSub = new BehaviorSubject<boolean>(false);
  public mocaLoadingSub$ = this.mocaLoadingSub.asObservable();

  constructor(
    private readonly _http: HttpClient,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly swalToastService: SwalToastService
  ) {}

  async getData() {
    this.mocaLoadingSub.next(true);
    await this.getMocaAssessments(
      this.patientService.patientIDsignal(),
      this.eocService.currentVisitId()
    ).subscribe({
      next: (response) => {
        this.mocaDataSub.next(response?.data!);
        this.mocaLoadingSub.next(false);
      },
      error: (err) => {
        this.mocaLoadingSub.next(false);
        this.swalToastService.toastError(
          'Unable to retrieve MoCA assessments'
        );
        console.error('Error fetching MoCA assessments:', err);
      },
    });
  }

  getMocaAssessments(
    patientId: string,
    visitId?: string
  ): Observable<IGenericServiceResponse<IVelesMocaAssessment[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    if (visitId) {
      return this._http.get<
        IGenericServiceResponse<IVelesMocaAssessment[]>
      >(
        `${environment.BACKEND_URL}VelesMocaAssessment/${patientId}?visitId=${visitId}`,
        { headers: headers }
      );
    } else {
      return this._http.get<
        IGenericServiceResponse<IVelesMocaAssessment[]>
      >(`${environment.BACKEND_URL}VelesMocaAssessment/${patientId}`, {
        headers: headers,
      });
    }
  }

  getPDF(assessmentId: string): Observable<Blob> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get(
      `${environment.BACKEND_URL}VelesMocaAssessment/DownloadPDF/${assessmentId}`,
      {
        headers: headers,
        responseType: 'blob',
      }
    );
  }

  downloadPDF(item: IVelesMocaAssessment): void {
    this._http
      .get(
        `${environment.BACKEND_URL}VelesMocaAssessment/DownloadPDF/${item.id}`,
        { responseType: 'blob' }
      )
      .subscribe({
        next: (blob) => {
          if (!ALLOWED_MIME.includes(blob.type as any)) {
            this.swalToastService.toastError('Unsupported file type.');
            return;
          }
          const path = item.path || '';
          const nameFromPath =
            path.substring(path.lastIndexOf('/') + 1) ||
            item.fileName ||
            'moca-assessment';
          const file = new Blob([blob], { type: blob.type });
          saveAs(file, nameFromPath);
        },
        error: () => {
          this.swalToastService.toastError('Unable to download the document');
        },
      });
  }

  addMocaAssessment(
    mocaObj: IAddVelesMocaAssessment
  ): Observable<any> {
    mocaObj.visitId = this.eocService.currentVisitId();
    mocaObj.patientId = this.patientService.patientIDsignal();

    const submit = () => {
      const formData = new FormData();
      formData.append('Score', mocaObj.score?.toString()!);
      formData.append('PatientId', mocaObj.patientId?.toString()!);
      formData.append('VisitId', mocaObj.visitId?.toString()!);
      if (mocaObj.file) {
        formData.append('File', mocaObj.file);
      }

      return this._http.post(
        `${environment.BACKEND_URL}VelesMocaAssessment`,
        formData
      );
    };

    if (!mocaObj.file) {
      return submit().pipe(
        catchError((err) => {
          this.swalToastService.toastError(
            'Unable to submit the MoCA assessment.'
          );
          throw err;
        })
      );
    }

    return from(validateUpload(mocaObj.file)).pipe(
      mergeMap((res) => {
        if (!res.ok) {
          this.swalToastService.toastError(res.error!);
          return EMPTY;
        }
        return submit();
      }),
      catchError((err) => {
        this.swalToastService.toastError(
          'Unable to submit the MoCA assessment.'
        );
        throw err;
      })
    );
  }

  deleteMocaAssessment(assessmentId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}VelesMocaAssessment/${assessmentId}`,
      { headers: headers }
    );
  }

  deleteMocaAssessmentFromTable(assessmentId: string) {
    this.mocaDataSub.next(
      this.mocaDataSub.value?.filter((item) => item.id !== assessmentId)
    );
  }
}
